import * as React from 'react';
import {Suspense, useDeferredValue, useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link} from 'react-router-dom';
import {Building2, Search, User} from 'lucide-react';
import {OpsOwnersPageQuery} from './__generated__/OpsOwnersPageQuery.graphql';
import {OpsOwnersPageCreateMutation} from './__generated__/OpsOwnersPageCreateMutation.graphql';
import {inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsOwnersPageQuery($search: String) {
        entities(search: $search, first: 200) {
            nodes {
                id
                kind
                name
                email
                phone
                ownedAssets { id assetTag name }
            }
        }
    }`;

const CREATE = graphql`
    mutation OpsOwnersPageCreateMutation($input: CreateEntityInput!) {
        createEntity(input: $input) {
            entity { id name }
            userErrors { message }
        }
    }`;

const KindIcon: React.FC<{ kind: string }> = ({kind}) =>
    kind === 'PERSON'
        ? <User className="w-4 h-4 text-gray-500" aria-label="Person"/>
        : <Building2 className="w-4 h-4 text-gray-500" aria-label="Company"/>;

const OwnerTable: React.FC<{ search: string; fetchKey: number }> = ({search, fetchKey}) => {
    const data = useLazyLoadQuery<OpsOwnersPageQuery>(QUERY, {search: search || null}, {
        fetchKey, fetchPolicy: 'network-only',
    });
    const rows = data.entities.nodes;
    if (rows.length === 0) return <p className="text-gray-500 text-sm">No owners yet.</p>;

    return (
        <div className="border border-gray-800 divide-y divide-gray-900">
            {rows.map((e) => (
                <div key={e.id} className="flex flex-col md:flex-row md:items-start gap-1 md:gap-6 px-4 py-3">
                    <span className="flex items-center gap-2 text-white font-semibold md:w-64">
                        <KindIcon kind={e.kind}/>{e.name}
                    </span>
                    <span className="text-gray-400 text-sm md:w-64 truncate">{e.email ?? '—'}</span>
                    <span className="text-gray-400 text-sm md:w-40">{e.phone ?? '—'}</span>
                    <span className="text-gray-500 text-xs flex-1">
                        {e.ownedAssets.length === 0 ? 'No assets' : e.ownedAssets.map((a, i) => (
                            <React.Fragment key={a.id}>
                                {i > 0 && ', '}
                                <Link to={`/ops/inventory/${encodeURIComponent(a.id)}`} className="font-mono hover:text-white" title={a.name}>{a.assetTag}</Link>
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            ))}
        </div>
    );
};

const NewOwnerForm: React.FC<{ onCreated: () => void }> = ({onCreated}) => {
    const [commit, inFlight] = useMutation<OpsOwnersPageCreateMutation>(CREATE);
    const blank = {kind: 'COMPANY', name: '', email: '', phone: ''};
    const [form, setForm] = useState(blank);
    const [errors, setErrors] = useState<string[]>([]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        commit({
            variables: {input: {kind: form.kind as 'PERSON' | 'COMPANY', name: form.name, email: form.email || null, phone: form.phone || null}},
            onCompleted: (res) => {
                const payload = res.createEntity;
                if (payload?.entity) {
                    setForm(blank);
                    setErrors([]);
                    onCreated();
                } else {
                    setErrors(payload?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                }
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    return (
        <form onSubmit={submit} className="flex flex-col md:flex-row gap-2">
            <select className={`${inputClass} w-32`} value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})}>
                <option value="COMPANY">Company</option>
                <option value="PERSON">Person</option>
            </select>
            <input className={`${inputClass} flex-1`} placeholder="Name" required value={form.name}
                   onChange={(e) => setForm({...form, name: e.target.value})}/>
            <input className={`${inputClass} flex-1`} placeholder="Email" type="email" value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}/>
            <input className={`${inputClass} w-40`} placeholder="Phone" value={form.phone}
                   onChange={(e) => setForm({...form, phone: e.target.value})}/>
            <button type="submit" disabled={inFlight} className={primaryButton}>Add owner</button>
            {errors.length > 0 && <p className="text-red-400 text-xs self-center">{errors.join(', ')}</p>}
        </form>
    );
};

// People and companies that own gear; assign them on each item's page.
const OpsOwnersPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const deferredSearch = useDeferredValue(search);
    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <h1 className="text-3xl font-display font-bold">Owners</h1>
                <label className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder="Search name, email, phone…" className={`${inputClass} pl-9 w-80`}/>
                </label>
            </div>
            <div className="mb-6 p-4 border border-gray-800 bg-black/40">
                <NewOwnerForm onCreated={refresh}/>
            </div>
            <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                <OwnerTable search={deferredSearch} fetchKey={fetchKey}/>
            </Suspense>
        </div>
    );
};

export default OpsOwnersPage;
